import React from "react";
import { createTheme } from "@mui/material/styles";
// Enables the MuiDatePicker / MuiTimePicker / MuiPickersPopper theme keys below
import type {} from "@mui/x-date-pickers/themeAugmentation";
import PrimaryArrowIcon from "../assets/icons/PrimaryArrowIcon";
import FigmaCalendarHeader from "../component/atoms/FigmaCalendarHeader";
import chevronDown from "../assets/icons/figma/chevron-down.svg";

// Shared panel look for every dropdown/popover (selects, autocompletes, pickers)
const panelShadow =
  "0 12px 32px rgba(15,23,42,0.14), 0 4px 10px rgba(15,23,42,0.06)";

// Figma design-system field border — light/dark aware, shared with the atoms
export const fieldBorder = (mode: "light" | "dark") =>
  mode === "light" ? "#DCE3E8" : "rgba(224,227,232,0.24)";

// Figma design-system field-label color — muted blue-gray in light mode, a
// lighter shade in dark mode so 14px labels keep readable contrast on black.
export const fieldLabelColor = (mode: "light" | "dark") =>
  mode === "light" ? "#6B7C8F" : "#A9BACB";

export const getTheme = (mode: "light" | "dark") =>
  createTheme({
    palette: {
      mode,
      primary: {
        main: "#3C7795",
        // Text on primary-colored fills (buttons, selected calendar cells).
        // Was "#0000001f" (12% black) — unreadable on #3C7795.
        contrastText: "#ffffff"
      },
      background: {
        default: mode === "light" ? "#F5F8FC" : "#000000",
        paper: mode === "light" ? "#ffffff" : "#000000",
      },
      text: {
        primary: mode === "light" ? "#000000" : "#E0E0E0",
        secondary: mode === "light" ? "rgba(66, 66, 66, 0.9)" : "#E0E0E0",
        disabled: mode === "light" ? "#717188" : "#000000",
      },
      common:{
        white: mode === "light" ? "#ffffff" : "rgb(13 13 13)",
      }
    },
    typography: {
      fontFamily: "poppins", // <-- global font
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            transition: "background-color 0.3s, color 0.3s",
            fontFamily: "Poppins", // <-- fallback for <body>
            color: mode === "light" ? "#2c2c2c" : "#E0E0E0",
          },
        },
      },
      // Figma design system defaults so raw MUI usage matches the common atoms:
      // 8px field radius, #DCE3E8 borders, #A9A9A9 placeholders. The base
      // border rule is kept at low specificity on purpose so MUI's own error
      // and disabled outlines, and any caller sx, still win.
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: "8px",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: fieldBorder(mode),
            },
            "&:hover:not(.Mui-disabled):not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
              borderColor: "#3C7795",
            },
            "&.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
              borderColor: "#3C7795",
            },
            // Focus is a ring, not just a 1px color change, so keyboard users
            // can tell focus from hover (same ring CustomDatePicker uses).
            "&.Mui-focused:not(.Mui-error)": {
              boxShadow: "0 0 0 3px rgba(60,119,149,0.12)",
            },
            // Select display areas share the field metric, with a gutter so
            // the text never runs under the chevron.
            "& .MuiSelect-select": {
              padding: "12px",
              paddingRight: "36px",
              minHeight: "unset",
            },
          },
          // Slot-level (low-specificity) rules: any caller sx overrides these.
          // One field metric everywhere: 12px padding + 12-13px font ≈ 43px tall.
          input: ({ theme }) => ({
            fontSize: "13px",
            [theme.breakpoints.up("lg")]: { fontSize: "12px" },
            "&::placeholder": { color: "#A9A9A9", opacity: 1 },
            "&:not(textarea)": { padding: "12px" },
          }),
        },
      },
      // Raw MUI Buttons match CustomButton metrics
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            borderRadius: "4px",
            fontSize: "14px",
            fontWeight: 400,
            boxShadow: "none",
            "&:hover": { boxShadow: "none" },
          },
        },
      },
      // Figma chevron on every Select (pages that pass their own icon keep
      // theirs). The chevron svg has a baked-in color, so disabled selects dim
      // it via opacity instead of the color rule an SvgIcon would honor.
      MuiSelect: {
        defaultProps: {
          IconComponent: PrimaryArrowIcon,
          // Figma: the menu hangs from the field's RIGHT edge (Selects that pass
          // their own MenuProps repeat these two origins)
          MenuProps: {
            anchorOrigin: { vertical: "bottom", horizontal: "right" },
            transformOrigin: { vertical: "top", horizontal: "right" },
          },
        },
        styleOverrides: {
          icon: {
            right: 12,
            "&.Mui-disabled": { opacity: 0.4 },
          },
        },
      },
      MuiAutocomplete: {
        defaultProps: {
          popupIcon: React.createElement("img", {
            src: chevronDown,
            alt: "",
            style: { width: 12, height: 7 },
          }),
        },
        styleOverrides: {
          paper: {
            borderRadius: "10px",
            boxShadow: panelShadow,
            "& .MuiAutocomplete-listbox": { padding: "4px" },
            // MUI's internal listbox rules out-rank the option slot overrides
            // below, so the selected/focused colors are restated here at
            // higher specificity for raw Autocompletes.
            "& .MuiAutocomplete-listbox .MuiAutocomplete-option.Mui-focused": {
              backgroundColor: "rgba(60,119,149,0.08)",
              color: "#3C7795",
            },
            '& .MuiAutocomplete-listbox .MuiAutocomplete-option[aria-selected="true"]': {
              backgroundColor: "#3C7795",
              color: "#fff",
            },
            '& .MuiAutocomplete-listbox .MuiAutocomplete-option[aria-selected="true"].Mui-focused, & .MuiAutocomplete-listbox .MuiAutocomplete-option[aria-selected="true"]:hover': {
              backgroundColor: "#2E5F78",
              color: "#fff",
            },
          },
          // DashboardV1 menu look for raw Autocompletes: 13px rows, 6px radius,
          // soft teal hover, solid #3C7795 selected.
          option: {
            fontSize: "13px",
            fontWeight: 500,
            borderRadius: "6px",
            "&.Mui-focused": { backgroundColor: "rgba(60,119,149,0.08)", color: "#3C7795" },
            "&:hover": { backgroundColor: "rgba(60,119,149,0.08)", color: "#3C7795" },
            '&[aria-selected="true"]': { backgroundColor: "#3C7795", color: "#fff" },
            '&[aria-selected="true"].Mui-focused, &[aria-selected="true"]:hover': {
              backgroundColor: "#2E5F78",
              color: "#fff",
            },
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: "10px",
            boxShadow: panelShadow,
          },
          // <Select> menus only (their list is role="listbox"): Figma option rows —
          // Poppins 400 14px / 100%, 10px 15px padding; the selected one is plain
          // #0B87AC text with a 20px #3C7795 check box at the row's end. Other
          // menus (action menus) keep the generic MenuItem look below.
          list: {
            '&[role="listbox"] .MuiMenuItem-root': {
              fontFamily: "Poppins, sans-serif",
              fontSize: 14,
              fontWeight: 400,
              lineHeight: 1,
              padding: "10px 15px",
              gap: 12,
            },
            '&[role="listbox"] .MuiMenuItem-root.Mui-selected': {
              backgroundColor: "transparent !important",
              color: "#0B87AC",
              fontWeight: 400,
            },
            '&[role="listbox"] .MuiMenuItem-root.Mui-selected::after': {
              content: '""',
              flex: "0 0 auto",
              marginLeft: "auto",
              width: 20,
              height: 20,
              borderRadius: "4px",
              background: `#3C7795 url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='11' height='8' viewBox='0 0 11 8' fill='none'><path d='M9.72727 1L3.72727 7L1 4.27273' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/></svg>") center / 11px 8px no-repeat`,
            },
            // the focus ring the menu gives the selected item on open is not a hover: no fill
            '&[role="listbox"] .MuiMenuItem-root.Mui-selected.Mui-focusVisible': {
              backgroundColor: "transparent !important",
              color: "#0B87AC",
            },
            '&[role="listbox"] .MuiMenuItem-root.Mui-selected:hover': {
              backgroundColor: "rgba(60,119,149,0.08) !important",
              color: "#0B87AC",
            },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            "&.Mui-selected": {
              backgroundColor: "#3C7795 !important",
              color: "#fff",
            },
            "&.Mui-selected:hover": {
              backgroundColor: "#2E5F78 !important",
              color: "#fff",
            },
            "&:hover": {
              backgroundColor: "rgba(60,119,149,0.08)",
            },
          },
        },
      },
      // Raw date/time pickers anywhere in the app always open the desktop
      // popover panel (never the fullscreen mobile "SELECT DATE" dialog) and
      // share the CustomDateRangePicker-style calendar: drill-down header,
      // single-letter weekdays, grayed adjacent-month days.
      MuiDatePicker: {
        defaultProps: {
          desktopModeMediaQuery: "@media all",
          showDaysOutsideCurrentMonth: true,
          fixedWeekNumber: 6,
          views: ["year", "month", "day"],
          openTo: "day",
          dayOfWeekFormatter: (day: any) =>
            (typeof day === "string" ? day : day.format("dd")).charAt(0).toUpperCase(),
          slots: { calendarHeader: FigmaCalendarHeader },
        },
      },
      MuiTimePicker: {
        defaultProps: { desktopModeMediaQuery: "@media all" },
      },
      MuiDateTimePicker: {
        defaultProps: { desktopModeMediaQuery: "@media all" },
      },
      // MUI X v8 picker fields render as MuiPickersOutlinedInput (not
      // MuiOutlinedInput), so the field metric is restated here for them —
      // raw pickers included.
      MuiPickersOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: "8px",
            height: "43px",
            fontSize: "12px",
            "& .MuiPickersOutlinedInput-notchedOutline": {
              borderColor: fieldBorder(mode),
            },
            "&:hover:not(.Mui-disabled):not(.Mui-error) .MuiPickersOutlinedInput-notchedOutline": {
              borderColor: "#3C7795",
            },
            "&.Mui-focused:not(.Mui-error) .MuiPickersOutlinedInput-notchedOutline": {
              borderColor: "#3C7795",
            },
            "&.Mui-focused:not(.Mui-error)": {
              boxShadow: "0 0 0 3px rgba(60,119,149,0.12)",
            },
          },
          sectionsContainer: {
            padding: 0,
          },
        },
      },
      MuiPickersPopper: {
        styleOverrides: {
          paper: {
            borderRadius: "10px",
            boxShadow: panelShadow,
            // Month/year grid cells (v8 class names — the MuiPickersMonth /
            // MuiPickersYear keys below cover older class generations).
            "& .MuiMonthCalendar-button, & .MuiYearCalendar-button": {
              fontSize: "12px",
              fontWeight: 500,
              borderRadius: "6px",
              "&.Mui-selected": {
                backgroundColor: "#3C7795",
                color: "#ffffff",
                fontWeight: 600,
              },
              "&.Mui-selected:hover, &.Mui-selected:focus": {
                backgroundColor: "#2E5F78",
              },
              "&:not(.Mui-selected):hover": {
                backgroundColor: mode === "dark" ? "rgba(60,119,149,0.16)" : "#f1f4f7",
                color: mode === "dark" ? "#E0E0E0" : "#2E5F78",
              },
            },
          },
        },
      },
      // Calendar cells: same fonts/radius/selected colors as the range picker.
      MuiPickersDay: {
        styleOverrides: {
          root: {
            fontSize: "12px",
            fontWeight: 500,
            borderRadius: "6px",
            "&.MuiPickersDay-dayOutsideMonth": {
              color: mode === "dark" ? "#4A4A55" : "#b9bec6",
            },
            "&.Mui-selected": {
              backgroundColor: "#3C7795",
              color: "#ffffff",
              fontWeight: 600,
            },
            "&.Mui-selected:hover, &.Mui-selected:focus": {
              backgroundColor: "#2E5F78",
            },
            "&:not(.Mui-selected):hover": {
              backgroundColor: mode === "dark" ? "rgba(60,119,149,0.16)" : "#f1f4f7",
              color: mode === "dark" ? "#E0E0E0" : "#2E5F78",
            },
          },
        },
      },
      MuiDayCalendar: {
        styleOverrides: {
          weekDayLabel: {
            fontSize: "12px",
            fontWeight: 500,
          },
        },
      },
      // v8 month/year grid cells (MuiPickersMonth/Year below cover older versions)
      MuiMonthCalendar: {
        styleOverrides: {
          root: {
            "& .MuiMonthCalendar-button": {
              fontSize: "12px",
              fontWeight: 500,
              borderRadius: "6px",
              "&.Mui-selected": { backgroundColor: "#3C7795", color: "#ffffff", fontWeight: 600 },
              "&.Mui-selected:hover, &.Mui-selected:focus": { backgroundColor: "#2E5F78" },
              "&:not(.Mui-selected):hover": {
                backgroundColor: mode === "dark" ? "rgba(60,119,149,0.16)" : "#f1f4f7",
                color: mode === "dark" ? "#E0E0E0" : "#2E5F78",
              },
            },
          },
        },
      },
      MuiYearCalendar: {
        styleOverrides: {
          root: {
            "& .MuiYearCalendar-button": {
              fontSize: "12px",
              fontWeight: 500,
              borderRadius: "6px",
              "&.Mui-selected": { backgroundColor: "#3C7795", color: "#ffffff", fontWeight: 600 },
              "&.Mui-selected:hover, &.Mui-selected:focus": { backgroundColor: "#2E5F78" },
              "&:not(.Mui-selected):hover": {
                backgroundColor: mode === "dark" ? "rgba(60,119,149,0.16)" : "#f1f4f7",
                color: mode === "dark" ? "#E0E0E0" : "#2E5F78",
              },
            },
          },
        },
      },
      MuiPickersMonth: {
        styleOverrides: {
          monthButton: {
            fontSize: "12px",
            borderRadius: "6px",
            "&.Mui-selected": { backgroundColor: "#3C7795", color: "#ffffff", fontWeight: 600 },
            "&.Mui-selected:hover, &.Mui-selected:focus": { backgroundColor: "#2E5F78" },
            "&:not(.Mui-selected):hover": {
              backgroundColor: mode === "dark" ? "rgba(60,119,149,0.16)" : "#f1f4f7",
              color: mode === "dark" ? "#E0E0E0" : "#2E5F78",
            },
          },
        },
      },
      MuiPickersYear: {
        styleOverrides: {
          yearButton: {
            fontSize: "12px",
            borderRadius: "6px",
            "&.Mui-selected": { backgroundColor: "#3C7795", color: "#ffffff", fontWeight: 600 },
            "&.Mui-selected:hover, &.Mui-selected:focus": { backgroundColor: "#2E5F78" },
            "&:not(.Mui-selected):hover": {
              backgroundColor: mode === "dark" ? "rgba(60,119,149,0.16)" : "#f1f4f7",
              color: mode === "dark" ? "#E0E0E0" : "#2E5F78",
            },
          },
        },
      },
      // Time panel columns: same selected color everywhere.
      MuiMultiSectionDigitalClockSection: {
        styleOverrides: {
          item: {
            fontSize: "12px",
            borderRadius: "6px",
            "&.Mui-selected": { backgroundColor: "#3C7795", color: "#ffffff", fontWeight: 600 },
            "&.Mui-selected:hover, &.Mui-selected:focus": { backgroundColor: "#2E5F78" },
            "&:not(.Mui-selected):hover": {
              backgroundColor: mode === "dark" ? "rgba(60,119,149,0.16)" : "#f1f4f7",
              color: mode === "dark" ? "#E0E0E0" : "#2E5F78",
            },
          },
        },
      },
    },
  });
